/**
 * Group creations that are queued to be saved but have not reached the wire yet, so an Undo (or a
 * Delete) can call them off.
 *
 * A group is created server-side by its own request (`POST /group`, or `/restructure`), which names
 * its children and so can only go out once their adds have landed. Between a group appearing on the
 * canvas and that request being sent there is a wait (a template's elements are added one request at
 * a time), and an Undo in that window removes the group and its children: sending the request then
 * would create an empty group, or fail on children that were never saved. Each pending creation holds
 * a token; whoever removes the group cancels it, and the request is only sent while its token is
 * still the current one.
 *
 * Tokens are per group id, and ids are unique to a group, so this is one store for the session.
 */
const currentTokens = new Map<string, symbol>()

/** Marks a group's creation as pending and returns its token; a later call replaces an older one. */
export function beginPendingGroupPersist(groupId: string): symbol {
  const token = Symbol(groupId)

  currentTokens.set(groupId, token)

  return token
}

/** True while `token` is still the group's current one: nobody has cancelled or replaced it. */
export function isPendingGroupPersistCurrent(groupId: string, token: symbol): boolean {
  return currentTokens.get(groupId) === token
}

/**
 * Settles the creation at the moment it is sent: true when `token` is still current (and it is then
 * spent), false when the creation was cancelled and must not be sent.
 */
export function claimPendingGroupPersist(groupId: string, token: symbol): boolean {
  if (currentTokens.get(groupId) !== token) return false

  currentTokens.delete(groupId)

  return true
}

/** How many creations are pending: a leak shows up as a count that never returns to zero. */
export function countPendingGroupPersists(): number {
  return currentTokens.size
}

/** Cancels the pending creation of every listed group (ids that are not groups are ignored). */
export function cancelPendingGroupPersists(ids: Iterable<string>): void {
  for (const id of ids) {
    currentTokens.delete(id)
  }
}
